// Mirrors the origin derived from extension/public/manifest.json's public key.
export const CODESMITH_EXTENSION_ORIGIN =
  "chrome-extension://hhncoojnkakjahbalbpbkbeihmlimmal";
export const CODESMITH_EXTENSION_CAPTURE_REQUEST =
  "codesmith-extension-capture-request";
export const CODESMITH_EXTENSION_CAPTURE_CANCEL =
  "codesmith-extension-capture-cancel";
export const CODESMITH_EXTENSION_CAPTURE_RESULT =
  "codesmith-extension-capture-result";
export const CODESMITH_EXTENSION_CAPTURE_ERROR =
  "codesmith-extension-capture-error";
export const CODESMITH_EXTENSION_CAPTURE_CANCELLED =
  "codesmith-extension-capture-cancelled";

export type CodesmithExtensionCaptureMode = "element" | "region";

export type CodesmithExtensionCaptureFile = {
  fileName: string;
  blob: Blob;
};

export type CodesmithExtensionCaptureResponse =
  | {
      type: typeof CODESMITH_EXTENSION_CAPTURE_RESULT;
      requestId: string;
      files: CodesmithExtensionCaptureFile[];
    }
  | {
      type: typeof CODESMITH_EXTENSION_CAPTURE_ERROR;
      requestId: string;
      message: string;
    }
  | {
      type: typeof CODESMITH_EXTENSION_CAPTURE_CANCELLED;
      requestId: string;
    };

export function requestCodesmithExtensionCapture(
  mode: CodesmithExtensionCaptureMode,
): string {
  const requestId = crypto.randomUUID();
  window.parent.postMessage(
    {
      type: CODESMITH_EXTENSION_CAPTURE_REQUEST,
      requestId,
      mode,
    },
    CODESMITH_EXTENSION_ORIGIN,
  );
  return requestId;
}

// Keyboard focus stays in the embedded dashboard while the page picker is up,
// so Escape is forwarded here and the extension dismisses the picker.
export function cancelCodesmithExtensionCapture(requestId: string): void {
  window.parent.postMessage(
    { type: CODESMITH_EXTENSION_CAPTURE_CANCEL, requestId },
    CODESMITH_EXTENSION_ORIGIN,
  );
}

function isCaptureFile(value: unknown): value is CodesmithExtensionCaptureFile {
  if (!value || typeof value !== "object") return false;
  const file = value as Record<string, unknown>;
  if (typeof file.fileName !== "string" || !(file.blob instanceof Blob)) {
    return false;
  }
  return file.blob.type === "image/png" || file.blob.type === "text/html";
}

export function parseCodesmithExtensionCaptureResponse(
  event: MessageEvent,
): CodesmithExtensionCaptureResponse | null {
  if (
    event.source !== window.parent ||
    event.origin !== CODESMITH_EXTENSION_ORIGIN ||
    !event.data ||
    typeof event.data !== "object"
  ) {
    return null;
  }

  const data = event.data as Record<string, unknown>;
  if (typeof data.requestId !== "string") return null;
  if (data.type === CODESMITH_EXTENSION_CAPTURE_RESULT) {
    if (!Array.isArray(data.files) || data.files.length === 0) return null;
    if (!data.files.every(isCaptureFile)) return null;
    if (!data.files.some((file) => file.blob.type === "image/png")) return null;
    return {
      type: CODESMITH_EXTENSION_CAPTURE_RESULT,
      requestId: data.requestId,
      files: data.files,
    };
  }
  if (data.type === CODESMITH_EXTENSION_CAPTURE_CANCELLED) {
    return {
      type: CODESMITH_EXTENSION_CAPTURE_CANCELLED,
      requestId: data.requestId,
    };
  }
  if (
    data.type === CODESMITH_EXTENSION_CAPTURE_ERROR &&
    typeof data.message === "string"
  ) {
    return {
      type: CODESMITH_EXTENSION_CAPTURE_ERROR,
      requestId: data.requestId,
      message: data.message,
    };
  }
  return null;
}
